import { createServerFn } from "@tanstack/react-start";
import type {
  ListContactThreadMessagesApiWorkspacesWorkspaceIdInboxContactThreadsGetParams,
  ListContactThreadMessagesResult,
  FetchThreadsMessagesRequest,
  FetchThreadsMessagesResult,
  AttachmentMeta,
} from "@/lib/api/generated";
import {
  fetchThreadsMessagesApiWorkspacesWorkspaceIdInboxMessagesPost,
  fetchEmailAttachmentsApiWorkspacesWorkspaceIdInboxThreadsThreadIdMessagesMessageIdAttachmentsGet,
} from "@/lib/api/generated/inbox/inbox";
import { customInstance } from "@/lib/api/custom-instance";
import { logInfo, logError } from "@/lib/structured-log";
import type {
  ApiInterfacesHttpDtoInboxSendMessageRequest,
  SendMessageResponse,
} from "@/lib/api/generated";
import { sendMessageApiWorkspacesWorkspaceIdInboxSendMessagePost } from "@/lib/api/generated/inbox/inbox";

export const listContactThreadMessagesServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      params?: ListContactThreadMessagesApiWorkspacesWorkspaceIdInboxContactThreadsGetParams;
    }) => data,
  )
  .handler(async ({ data }): Promise<ListContactThreadMessagesResult> => {
    const { workspaceId, params } = data;

    try {
      logInfo("inbox.list_contact_thread_messages.called", {
        workspace_id: workspaceId,
        page: params?.page ?? 1,
        page_size: params?.page_size ?? 50,
      });

      const url = `/api/workspaces/${workspaceId}/inbox/contact-threads`;
      const res = await customInstance<{ data: ListContactThreadMessagesResult; status: number }>(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            page: params?.page ?? 1,
            page_size: params?.page_size ?? 50,
            exclude_stages:
              params && (params as unknown as { exclude_stages?: string[] }).exclude_stages,
            include_stage:
              params && (params as unknown as { include_stage?: string }).include_stage,
            company_name: params && (params as unknown as { company_name?: string }).company_name,
            contact_name: params && (params as unknown as { contact_name?: string }).contact_name,
          }),
        },
      );

      if (res.status !== 200) {
        logError("inbox.list_contact_thread_messages.non_ok", {
          status: res.status,
        });
        throw new Error(`Failed to list inbox contacts: ${res.status}`);
      }

      const result = res.data as ListContactThreadMessagesResult;

      logInfo("inbox.list_contact_thread_messages.ok", {
        total: result.total,
        page: result.page,
        page_size: result.page_size,
        total_pages: result.total_pages,
        items_count: result.items?.length ?? 0,
      });

      return result;
    } catch (error) {
      logError("inbox.list_contact_thread_messages.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export const fetchThreadsMessagesServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; request: FetchThreadsMessagesRequest }) => data)
  .handler(async ({ data }): Promise<FetchThreadsMessagesResult> => {
    const { workspaceId, request } = data;

    try {
      logInfo("inbox.fetch_threads_messages.called", {
        workspace_id: workspaceId,
        threads_count: request.threads.length,
      });

      const res = await fetchThreadsMessagesApiWorkspacesWorkspaceIdInboxMessagesPost(
        workspaceId,
        request,
      );

      if (res.status !== 200) {
        logError("inbox.fetch_threads_messages.non_ok", {
          status: res.status,
        });
        throw new Error(`Failed to fetch thread messages: ${res.status}`);
      }

      const result = res.data as FetchThreadsMessagesResult;

      logInfo("inbox.fetch_threads_messages.ok", {
        total: result.total,
      });

      return result;
    } catch (error) {
      logError("inbox.fetch_threads_messages.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export const sendMessageServer = createServerFn({ method: "POST" })
  .validator((data) => data)
  .handler(async ({ data }): Promise<SendMessageResponse> => {
    const { workspaceId, request } = data as {
      workspaceId: string;
      request: ApiInterfacesHttpDtoInboxSendMessageRequest;
    };
    try {
      logInfo("inbox.send_message.called", {
        workspace_id: workspaceId,
        thread_id: request.thread_id,
        thread_type: request.thread_type,
        attachments_count: Array.isArray(request.attachments) ? request.attachments.length : 0,
      });
      const res = await sendMessageApiWorkspacesWorkspaceIdInboxSendMessagePost(
        workspaceId,
        request,
      );
      if (res.status !== 200) {
        logError("inbox.send_message.non_ok", { status: res.status });
        throw new Error(`Failed to send message: ${res.status}`);
      }
      const result = res.data as SendMessageResponse;
      logInfo("inbox.send_message.ok", {
        thread_id: result.thread_id,
        message_id: result.message_id,
      });
      return result;
    } catch (error) {
      logError("inbox.send_message.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });

export const fetchEmailAttachmentsServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; threadId: string; messageId: string }) => data)
  .handler(async ({ data }): Promise<AttachmentMeta[]> => {
    const { workspaceId, threadId, messageId } = data;
    try {
      logInfo("inbox.fetch_email_attachments.called", {
        workspace_id: workspaceId,
        thread_id: threadId,
        message_id: messageId,
      });

      const res =
        await fetchEmailAttachmentsApiWorkspacesWorkspaceIdInboxThreadsThreadIdMessagesMessageIdAttachmentsGet(
          workspaceId,
          threadId,
          messageId,
        );

      if (res.status !== 200) {
        logError("inbox.fetch_email_attachments.non_ok", { status: res.status });
        throw new Error(`Failed to fetch email attachments: ${res.status}`);
      }

      const result = res.data as AttachmentMeta[];

      logInfo("inbox.fetch_email_attachments.ok", {
        count: result.length,
      });

      return result;
    } catch (error) {
      logError("inbox.fetch_email_attachments.error", {
        error: error instanceof Error ? error.message : "unknown-error",
      });
      throw error;
    }
  });
